// Single source of truth for clean, language-prefixed URLs.
// LV is the default language; both languages are mounted under /lv/... and /en/...

export type Lang = "lv" | "en";
export const LANGS: Lang[] = ["lv", "en"];

export const DEFAULT_LANG: Lang = "lv";

// Canonical product types (matches `products.product_type` in the DB).
export type ProductType =
  | "rackets"
  | "shoes"
  | "apparel"
  | "bags"
  | "balls"
  | "accessories";

// Type → URL slug per language.
export const TYPE_SLUGS: Record<Lang, Record<ProductType, string>> = {
  lv: {
    rackets: "padel-raketes",
    shoes: "padel-apavi",
    apparel: "padel-apgerbs",
    bags: "padel-somas",
    balls: "padel-bumbas",
    accessories: "padel-aksesuari",
  },
  en: {
    rackets: "padel-rackets",
    shoes: "padel-shoes",
    apparel: "padel-apparel",
    bags: "padel-bags",
    balls: "padel-balls",
    accessories: "padel-accessories",
  },
};

// Reverse lookup table built once at module load.
const SLUG_TO_TYPE: Record<string, ProductType> = (() => {
  const map: Record<string, ProductType> = {};
  for (const lang of LANGS) {
    const langMap = TYPE_SLUGS[lang];
    (Object.keys(langMap) as ProductType[]).forEach((t) => {
      map[langMap[t]] = t;
    });
  }
  return map;
})();

export function slugToType(slug: string): ProductType | null {
  return SLUG_TO_TYPE[slug.toLowerCase()] ?? null;
}

export function typeToSlug(type: ProductType, lang: Lang): string {
  return TYPE_SLUGS[lang][type];
}

// Static, non-catalog pages with localized slugs (admin URLs intentionally NOT here).
export const STATIC_SLUGS: Record<Lang, Record<string, string>> = {
  lv: {
    home: "",
    cart: "grozs",
    auth: "ienakt",
    account: "konts",
    contact: "kontakti",
    faq: "biezi-uzdotie-jautajumi",
    shipping: "piegade-un-atgriesana",
    terms: "noteikumi",
    privacy: "privatuma-politika",
    sale: "izpardosana",
    giftCard: "davanu-karte",
    subscription: "abonements",
    orderSuccess: "pasutijums/veiksmigs",
    news: "jaunumi",
    product: "p",
    courts: "laukumi",
  },
  en: {
    home: "",
    cart: "cart",
    auth: "login",
    account: "account",
    contact: "contact",
    faq: "faq",
    shipping: "shipping-returns",
    terms: "terms",
    privacy: "privacy",
    sale: "sale",
    giftCard: "gift-card",
    subscription: "subscription",
    orderSuccess: "order/success",
    news: "news",
    product: "p",
    courts: "courts",
  },
};

export type StaticKey = keyof (typeof STATIC_SLUGS)["lv"];

export function localizedPath(lang: Lang, key: StaticKey, rest?: string): string {
  const slug = STATIC_SLUGS[lang][key] ?? "";
  const base = slug ? `/${lang}/${slug}` : `/${lang}`;
  if (rest) return `${base}/${rest.replace(/^\//, "")}`;
  return base;
}

export function catalogPath(lang: Lang, type: ProductType, brand?: string | null): string {
  const typeSlug = TYPE_SLUGS[lang][type];
  const base = `/${lang}/${typeSlug}`;
  return brand ? `${base}/${brandSlug(brand)}` : base;
}

export function productPath(lang: Lang, slug: string): string {
  return `/${lang}/${slug}`;
}

export function brandSlug(name: string): string {
  return String(name || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

// Best-effort lang detection from a pathname like "/en/foo" or "/lv".
export function detectLangFromPath(pathname: string): Lang | null {
  const seg = pathname.split("/").filter(Boolean)[0]?.toLowerCase();
  return seg === "lv" || seg === "en" ? seg : null;
}

// Swap the language segment of a path, translating known catalog and static slugs.
export function switchLangInPath(pathname: string, target: Lang): string {
  const parts = pathname.split("/").filter(Boolean);
  const current = parts[0]?.toLowerCase();
  if (current !== "lv" && current !== "en") {
    return `/${target}${pathname.startsWith("/") ? pathname : `/${pathname}`}`;
  }
  const from: Lang = current as Lang;
  parts[0] = target;

  // Translate a known type slug if present at index 1.
  if (parts[1]) {
    const t = slugToType(parts[1]);
    if (t) parts[1] = TYPE_SLUGS[target][t];
  }

  // Translate known static slugs at index 1.
  if (parts[1]) {
    const staticKey = (Object.keys(STATIC_SLUGS[from]) as StaticKey[]).find(
      (k) => STATIC_SLUGS[from][k] === parts[1],
    );
    if (staticKey) parts[1] = STATIC_SLUGS[target][staticKey] || parts[1];
  }

  return "/" + parts.join("/");
}
